import { AuditTrailAction } from '../../audit/domain/auditTrailAction';
import { DashboardComponentType } from '../../dashboards/domain/dashboardComponentType';
import { LogSorterColumnPlacement } from '../../logSorter/domain/logSorterBinLayout';
import { LogSorterNumberSequence } from '../../logSorter/domain/logSorterNumberSequence';
import { Anchor } from '../../map/domain/anchor';
import { ProductionOrderState } from '../../productionOrders/domain/productionOrderState';
import { ProductType } from '../../products/domain/productType';
import { ReportType } from '../../reports/domain/reportType';
import { ShapeMode } from '../../shapes/domain/shapeMode';
import { StorageAreaStatus } from '../../storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from '../../storageAreas/domain/storageAreaType';
import { VehicleType } from '../../vehicles/domain/vehicleType';
import { VehicleSymbol } from '../../vehicles/domain/vehicleSymbol';
import { KeyboardActions } from '../../vehicles/domain/keyboardActions';
import { Unit } from '../../common/domain/bulkUnits';
import type { Translations } from '../domain/translations';
import { DeliveryStatus } from '../../deliveries/domain/deliveryStatus';

export const czech: Translations = {
  generic: {
    active: 'Aktivní',
    language: 'Jazyk',
    yes: 'Ano',
    no: 'Ne',
    save: 'Uložit',
    cancel: 'Zrušit',
    delete: 'Odstranit',
    id: 'ID',
    name: 'Název',
    description: 'Popis',
    type: 'Typ',
    code: 'Kód',
    quality: 'Kvalita',
    columns: 'Sloupce',
    color: 'Barva',
    capacity: 'Kapacita',
    loadingTableData: 'Načítání...',
    search: 'Hledat',
    generalErrorMsg: 'Jejda! Došlo k chybě',
    signIn: 'Přihlásit se',
    signOut: 'Odhlásit se',
    width: 'Šířka',
    length: 'Délka',
    resetColumns: 'Obnovit sloupce',
    height: 'Výška',
    login: 'Přihlášení',
    logout: 'Odhlášení',
    next: 'Další',
    back: 'Zpět',
    abort: 'Zrušit',
    close: 'Zavřít',
    searchBy: 'Hledat podle',
    clear: 'Vymazat',
    error: 'Chyba',
    success: 'Úspěšné',
    updated: 'Aktualizováno',
    added: 'Přidáno',
    taskCompleted: 'Úkol dokončen',
    created: 'Vytvořeno',
    deleted: 'Odstraněno',
    toggle: 'Přepnout',
    status: 'Stav',
    position: 'Poloha',
    direction: 'Směr',
    anchor: 'Kotva',
    product: 'Produkt',
    vehicle: 'Vozidlo',
    map: 'Mapa',
    satellite: 'Satelit',
    settings: 'Nastavení',
    geofence: 'Geofence',
    geofenceTop: 'Horní hranice geofence',
    geofenceBottom: 'Dolní hranice geofence',
    geofenceLeft: 'Levá hranice geofence',
    geofenceRight: 'Pravá hranice geofence',
    storageArea: 'Skladovací prostor',
    inventory: 'Zásoby',
    noAvailableOptions: 'Žádné dostupné možnosti',
    edit: 'Upravit',
    select: 'Vybrat',
    default: 'Výchozí',
    mirrored: 'Zrcadlené',
    add: 'Přidat',
    top: 'Nahoru',
    bottom: 'Dole',
    left: 'Vlevo',
    right: 'Vpravo',
    longitude: 'Délka',
    latitude: 'Šířka',
    rotation: 'Otočení',
    shape: 'Tvar',
    inside: 'Uvnitř',
    outside: 'Venku',
    logSorter: 'Třídič polen',
    confirm: 'Potvrdit',
    on: 'Zapnuto',
    off: 'Vypnuto',
    circle: 'Kruh',
    polygon: 'Mnohoúhelník',
    size: 'Velikost',
    rotate: 'Otočit',
    freeform: 'Volný tvar',
    rectangle: 'Obdélník',
    empty: 'Prázdné',
    discard: 'Zrušit',
    audit: 'Audit',
    export: 'Exportovat',
    import: 'Importovat',
    take: 'Vzít',
    ok: 'OK',
    from: 'Od',
    to: 'Do',
    move: 'Přesunout',
    coil: 'Svinout',
    swap: 'Vyměnit',
    copy: 'Kopírovat',
    copyOfName: (name: string) => `${name} (Copy)`,
    pick: 'Vybrat',
    unknown: 'Neznámé',
    enabled: 'Povoleno',
    note: 'Poznámka',
    groups: 'Skupiny',
    productGroup: 'Skupina produktů',
    products: 'Produkty',
    numberOfLogs: 'Počet kmenů',
    balance: 'Zůstatek',
    fifo: 'FIFO',
    volume: 'Objem',
    numberOfProductsInStorage: 'Počet produktů',
    storageAreas: 'Skladové prostory',
    showMore: 'Zobrazit více',
    preview: 'Náhled',
    selectColumns: 'Vybrat sloupce',
    locked: 'Zamčeno',
    unlocked: 'Odemčeno',
    keybindings: 'Klávesové zkratky',
    productionOrder: 'Výrobní zakázka',
    resource: 'Zdroje',
    priority: 'Priorita',
    amount: 'Množství',
    newVersion: 'Nová verze SILog',
    important: 'Důležité',
    noData: 'Žádná data',
    total: 'Celkem',
    calculate: 'Vypočítat',
    sorted: 'Seřazeno',
    unsorted: 'Neseřazeno',
    unit: 'Jednotka',
    content: 'Obsah',
    inTotal: 'celkem',
    reset: 'Obnovit',
    resetToDefault: 'Obnovit výchozí nastavení',
    resetLayoutConfirmHeader: 'Obnovit rozložení',
    resetLayoutConfirmMessage:
      'Obnovit výchozí rozložení dialogového okna? Velikost a poloha okna zůstanou zachovány.',
    customer: 'Zákazník',
    customerReference: 'Referenční číslo zákazníka',
    user: 'Uživatel',
    weight: 'Hmotnost',
    filter: 'Filtrovat',
  },
  application: {
    exit: 'Ukončit',
    exitSilog: 'Ukončit SILog',
    confirmExit: 'Opravdu chcete ukončit SILog?',
    openSettings: 'Otevřít nastavení aplikace',
  },
  sites: {
    actions: {
      fetchMany: {
        title: 'Načítání lokalit',
        error: 'Načítání lokalit se nezdařilo',
      },
    },
    notFound: 'Lokalita nebyla nalezena',
  },
  sideMenu: {
    home: 'Domů',
    map: 'Mapa',
    vehicles: 'Vozidla',
    products: 'Produkty',
    packages: 'Balíky',
    packaging: 'Balení',
    packageTypes: 'Typy balíků',
    styles: 'Styly',
    coils: 'Cívky',
    systemsettings: 'Systémová nastavení',
    dashboards: 'Přehledy',
    productionOrders: 'Výrobní zakázky',
    reports: 'Zprávy',
    deliveries: 'Dodávky',
  },
  vehicleType(type: VehicleType): string {
    switch (type) {
      case VehicleType.Undefined:
        return 'Nedefinováno';
      case VehicleType.Automatic:
        return 'Automatické';
      case VehicleType.LogHandler:
        return 'Manipulátor s kmeny';
      case VehicleType.Truck:
        return 'Nákladní vozidlo';
      case VehicleType.CoilForklift:
        return 'Vysokozdvižný vozík na svitky';
      case VehicleType.BulkHandler:
        return 'Manipulátor s sypkými materiály';
      case VehicleType.PackageHandler:
        return 'Manipulátor s balíky';
    }
  },
  vehicleSymbol(symbol: VehicleSymbol): string {
    switch (symbol) {
      case VehicleSymbol.Undefined:
        return 'Šipka (výchozí)';
      case VehicleSymbol.LogStacker:
        return 'Stohovač kmenů';
      case VehicleSymbol.WheelLoaderFrontGrapple:
        return 'Kolejový nakladač – přední drapák';
      case VehicleSymbol.WheelLoaderHighGrapple:
        return 'Kolejový nakladač – vysoký drapák';
      case VehicleSymbol.TimberCrane:
        return 'Dřevěný jeřáb';
      case VehicleSymbol.CoilForklift:
        return 'Vysokozdvižný vozík na svitky';
    }
  },
  productType(type: ProductType): string {
    switch (type) {
      case ProductType.Log:
        return 'Kmen';
      case ProductType.Undefined:
        return 'Nedefinováno';
      case ProductType.Coil:
        return 'Cívka';
      case ProductType.Bulk:
        return 'Sypký materiál';
      case ProductType.Dimension:
        return 'Rozměry';
    }
  },
  products: {
    mapping: {
      matchCriteria: 'Kritéria shody',
      target: 'Cíl',
      created: 'Vytvořeno',
      importRow: 'Řádek importu',
      sourceProduct: 'Zdrojový produkt',
      sourceData: 'Zdrojová data',
      result: 'Výsledek',
      correction: 'Korekce',
    },
    actions: {
      filterByProduct: 'Filtrovat podle produktu',
      create: {
        title: 'Vytváření produktu',
        error: 'Vytvoření produktu se nezdařilo',
      },
      update: {
        title: 'Aktualizace produktu',
        error: 'Aktualizace produktu se nezdařila',
      },
      delete: {
        title: 'Odstranění produktu',
        error: 'Odstranění produktu se nezdařilo',
        statusCodes: {
          409: 'Produkt je stále používán ve skladu',
        },
      },
      fetch: {
        title: 'Načítání produktu',
        error: 'Načítání produktu se nezdařilo',
      },
      fetchMany: {
        title: 'Načítání produktů',
        error: 'Načítání produktů se nezdařilo',
      },
      editLogYardProducts(number: number) {
        return `Edit ${number} logyard products`;
      },
      editCoilProducts(number: number) {
        return `Edit ${number} coil products`;
      },
      deleteMultipleProducts(number: number) {
        return `Delete ${number} products`;
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Delete product ${name}`;
      },
      description: 'Opravdu chcete tento produkt odstranit? Tuto akci nelze vrátit zpět.',
      deleteButton: 'Ano, odstranit produkt',
    },

    products: 'Produkty',
    addNewProduct: 'Přidat nový produkt',
    editProduct: 'Upravit produkt',
    editMultipleProducts(number: number) {
      return `Edit ${number} products`;
    },
    fetchDataError: 'Načtení údajů o produktu se nezdařilo',
    addNewProductError: 'Produkt se nepodařilo vytvořit',
    editProductError: 'Produkt se nepodařilo aktualizovat',
    deleteProductError: 'Produkt se nepodařilo smazat:',
    noProductsFound: 'Nebyly nalezeny žádné produkty.',
    loadingProductData: 'Načítání údajů o produktu...',
    logsPerSquareMeter: 'kmeny/m²',
    notFound: 'Produkt nebyl nalezen',
    couldNotFetchProduct: 'Produkt se nepodařilo načíst!',
    logData: {
      logData: 'Údaje o kmenech',
      logsPerSquareMeter: 'Počet kmenů na m²',
      minDiameter: 'Minimální průměr',
      maxDiameter: 'Maximální průměr',
      maxLength: 'Maximální délka',
      minLength: 'Minimální délka',
      averageCylinderVolume: 'Průměrný objem válce',
      averagePhysicalVolume: 'Průměrný fyzický objem',
      isSorted: 'Je seřazeno',
      species: 'Druh',
      logSorterName: 'Název třídiče kmenů',
      sawLineName: 'Název pilařské linky',
      logsPerSquareMeterDescription: 'Slouží k odhadu, kolik kmenů může vozidlo odvézt ze skladu',
    },
    coilData: {
      title: 'Svitek',
      diameter: 'Průměr',
      depth: 'Hloubka',
    },
    bulkData: {
      title: 'Objem',
      unit: 'Jednotka',
      density: 'Hustota',
    },
    dimensionData: {
      title: 'Rozměry',
      actualDimensions: 'Skutečné rozměry',
      nominalDimensions: 'Nominální rozměry',
      density: 'Hustota',
      thickness: 'Tloušťka',
      width: 'Šířka',
      length: 'Délka',
      moisture: 'Vlhkost',
    },
    importDialog: {
      title: 'Import CSV',
      productType: 'Typ produktu',
      selectType: 'Vyberte typ',
      downloadTemplate: 'Stáhnout šablonu pro import',
      choose: 'Vybrat',
      upload: 'Nahrát',
      uploadedFilePrefix: 'Nahraný soubor:',
      selectTypeBeforeFile: 'Před výběrem souboru prosím vyberte typ produktu.',
      failedToParseCsvPrefix: 'Nepodařilo se analyzovat CSV:',
      failedToParseCsvFile: 'Nepodařilo se analyzovat soubor CSV.',
      missingRequiredColumns: 'Chybí povinné sloupce:',
      invalidRowsOf: 'z',
      invalidRowsHaveErrors:
        'produktů vykazují chyby při ověření. Pro zobrazení podrobností najeďte kurzorem na',
      hoverIconForCellDetails: 'ikonu v buňce.',
      allProductsValidPrefix: 'Všechny',
      allProductsValidSuffix: 'produkty vypadají platně.',
      noDataLoaded: 'Zatím nebyla načtena žádná data. Vyberte výše soubor CSV.',
      status: 'Stav',
      importAborted: 'Import byl přerušen.',
      imported: 'Importované',
      productsWord: 'produkty.',
      summaryFailed: 'selhal — najděte kurzor na',
      hoverIconForStatusDetails: 'ikonu ve sloupci Stav pro podrobnosti.',
      close: 'Zavřít',
      import: 'Import',
      abort: 'Zrušit',
      columnProductName: 'Název produktu',
      columnDescription: 'Popis',
      columnExternalCode: 'Externí kód',
      columnColor: 'Barva',
      columnQuality: 'Kvalita',
      columnSpecies: 'Druh',
      columnLogsPerSquareMeter: 'Kmeny/m²',
      columnMinDiameter: 'Min. průměr',
      columnMaxDiameter: 'Max. průměr',
      columnMaxLength: 'Max. délka',
      columnMinLength: 'Min. délka',
      columnAverageCylinderVolume: 'Průměrný objem válce',
      columnAveragePhysicalVolume: 'Průměrný fyzický objem',
      columnSorted: 'Tříděno',
      columnLogSorter: 'Třídič kmenů',
      columnSawLine: 'Pila',
      columnDiameter: 'Průměr',
      columnDepth: 'Hloubka',
      columnDensity: 'Hustota',
      columnUnit: 'Jednotka',
      validationRequired: 'je povinná.',
      validationMustBeNumber: 'musí být číslo.',
      validationMustBeInteger: 'musí být celé číslo.',
      validationMustBeBoolean: 'musí být TRUE nebo FALSE.',
      validationMustBeHexColor: 'musí být hexadecimální barva (např. #4d8aeb).',
      validationMustBeOneOf: 'musí být jedna z následujících možností:',
      validationMustBeAtLeast: 'musí být alespoň:',
      duplicateProductNameInFile: 'Duplicitní název produktu v tomto souboru.',
      productNameAlreadyExists: 'Produkt s tímto názvem již existuje.',
      importFailedFallback: 'Import se nezdařil.',
    },
    deleteMultipleDialog: {
      deleteProducts: 'Odstranit produkty',
      areYouSure: 'Opravdu chcete tyto produkty odstranit?',
      thisActionCannotBeUndone: 'Tuto akci nelze vrátit zpět.',
      deleted: 'Odstraněno',
      products: 'produkty',
      summaryFailed: 'selhalo — najděte kurzor na',
      hoverIconForDetails: 'ikonu ve sloupci Stav pro podrobnosti.',
      deleteAborted: 'Operace odstranění byla přerušena.',
      type: 'Zadejte',
      DELETE: 'DELETE',
      toConfirm: 'pro potvrzení',
      noProductsSelected: 'Nebyly vybrány žádné produkty',
      deleteFailed: 'Odstranění se nezdařilo',
    },
  },
  productGroups: {
    actions: {
      create: {
        title: 'Vytváření skupiny produktů',
        error: 'Vytvoření skupiny produktů se nezdařilo',
      },
      update: {
        title: 'Aktualizace skupiny produktů',
        error: 'Aktualizace skupiny produktů se nezdařila',
      },
      delete: {
        title: 'Odstranění skupiny produktů',
        error: 'Odstranění skupiny produktů se nezdařilo',
      },
      fetch: {
        title: 'Načítání skupiny produktů',
        error: 'Načtení skupiny produktů se nezdařilo',
      },
      fetchMany: {
        title: 'Načítání skupin produktů',
        error: 'Načtení skupin produktů se nezdařilo',
      },
    },
    listPage: {
      title: 'Skupiny produktů',
      empty: 'Žádné skupiny produktů',
      loading: 'Načítání skupin produktů...',
      addNew: 'Přidat novou skupinu',
      edit: 'Upravit skupinu',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete product group ${name}`;
      },
      description: 'Opravdu chcete tuto skupinu produktů smazat? Tuto akci nelze vrátit zpět.',
      deleteButton: 'Ano, smazat skupinu produktů',
    },
  },
  vehicles: {
    panels: {
      identity: 'Identita',
      preview: 'Náhled',
      type: 'Typ',
      movement: 'Pohyb',
      automatic: 'Automatický',
      keybindings: 'Klávesové zkratky',
    },
    actions: {
      create: {
        title: 'Vytváření vozidla',
        error: 'Vytvoření vozidla se nezdařilo',
      },
      update: {
        title: 'Aktualizace vozidla',
        error: 'Aktualizaci vozidla se nepodařilo provést',
      },
      delete: {
        title: 'Odstranění vozidla',
        error: 'Odstranění vozidla se nepodařilo',
      },
      fetch: {
        title: 'Načítání vozidla',
        error: 'Načítání vozidla se nepodařilo',
      },
      fetchMany: {
        title: 'Načítání vozidel',
        error: 'Načítání vozidel se nepodařilo',
      },
      startReservation: {
        title: 'Zahájení rezervace vozidla',
        error: 'Rezervaci vozidla se nepodařilo provést',
      },
      endReservation: {
        title: 'Ukončení rezervace vozidla',
        error: 'Ukončení rezervace vozidla se nepodařilo',
      },
      updateKeybinds: {
        title: 'Aktualizace klávesových zkratek vozidla',
        error: 'Aktualizaci klávesových zkratek vozidla se nepodařilo provést',
      },
    },
    vehicles: 'Vozidla',
    noVehiclesFound: 'Nebyly nalezena žádná vozidla.',
    loadingVehicleData: 'Načítání údajů o vozidle...',
    addNewVehicle: 'Přidat nové vozidlo',
    editVehicle: 'Upravit vozidlo',
    confirmDeleteMsg: 'Opravdu chcete toto vozidlo smazat? Tuto akci nelze vrátit zpět.',
    deleteBtnTxt: 'Ano, smazat vozidlo',
    fetchDataError: 'Načtení dat vozidla se nezdařilo',
    editVehicleSuccess: 'Upravováno',
    addNewVehicleSuccess: 'Úspěšně: přidáno',
    deleteVehicleSuccess: 'Úspěšně: smazáno',
    deleteVehicleError: 'Vozidlo se nepodařilo smazat:',
    nameMustBeUnique: 'Název musí být jedinečný',
    symbol: 'Symbol',
    symbolPreview: 'Náhled',
    anchorPoint: 'Kotevní bod',
    anchorRotationOffset: 'Posun rotace',
    anchorRotationOffsetDescription:
      'Rotace ve směru hodinových ručiček aplikovaná na ikonu vozidla a na směr přiblížení, relativně k GPS kurzu. Pro žádný posun ponechte hodnotu 0.',
    usePickupPoint: 'Samostatný bod vyzvednutí',
    usePickupPointDescription:
      'Je-li tato možnost povolena, kontroly blízkosti a oblasti pro kmeny nebo svitky začínají od samostatného bodu vyzvednutí na ikoně namísto kotevního bodu.',
    pickupPoint: 'Bod vyzvednutí',
    capacityDescription:
      'Slouží k odhadu, kolik kmenů může toto vozidlo odvézt ze skladovacího prostoru. Bude se používat v kombinaci s produkty „kmeny na metr čtvereční“.',
    packageCapacity: 'Kapacita balíků',
    packageCapacityDescription:
      'Maximální počet balíků, které může toto vozidlo přepravit najednou.',
    proximityRange: 'Dosah detekce blízkosti',
    proximityRangeDescription: 'Dosah detekce pro vyzvednutí a odložení balíku.',
    proximityConeAngle: 'Kužel detekce blízkosti',
    proximityConeAngleDescription:
      'Poloviční úhel přístupového kužele pro vyzvednutí a odložení balíku. Vozidlo se musí k danému místu přiblížit z oblasti v rámci tohoto úhlu.',
    headingSmoothingTime: 'Vyhlazování směru jízdy',
    headingSmoothingTimeDescription:
      'Časová konstanta pro vyhlazování směru jízdy vozidla za účelem snížení kolísání GPS. Vyšší hodnoty znamenají plynulejší, ale pomalejší reakci. Nechte pole prázdné, pokud nechcete vyhlazování použít (směr jízdy se řídí přímo podle GPS).',
    stationarySpeedThreshold: 'Prahová hodnota rychlosti při stání',
    stationarySpeedThresholdDescription:
      'Pod touto rychlostí je vozidlo považováno za stojící a jeho směr jízdy se udržuje konstantní namísto sledování nespolehlivého směru z GPS. Nechte pole prázdné pro výchozí hodnotu 0,5 km/h. Nemá žádný vliv, pokud je povoleno otáčení na místě.',
    neutralTurn: 'Otáčení na místě',
    neutralTurnDescription: 'Vozidlo se může otáčet na místě.',
    packagePickupDirection: 'Směr vyzvednutí zásilky',
    packagePickupDirectionDescription:
      'Z které strany zásilky toto vozidlo zásilky vyzvedává a odkládá.',
    packagePickupDirectionAny: 'Z jakékoli strany',
    packagePickupDirectionLengthSide: 'Podélná strana (vlevo/vpravo)',
    packagePickupDirectionWidthSide: 'Příčná strana (konce)',
    placementPreview: 'Náhled umístění',
    placementPreviewDescription:
      'Zobrazí poloprůhledný náznak plánovaných pozic balíků před jejich odložením.',
    deleteVehicle(name: string) {
      return `Delete vehicle ${name}`;
    },
    inUseDialog: {
      header: 'Vozidlo je v provozu!',
    },
    reservationEnded: 'Někdo jiný převzal řízení vašeho vozidla!',
    reservationEndedDescription: (userId: string) =>
      `Your vehicle was taken by ${userId.split('|')[0]}`,
    automaticLogYardMoves: 'Automatické pohyby na dvoře',
    automaticLogYardMovesLogSorterBinFull: 'Když je zásobník třídiče kulatiny plný',
    automaticLogYardMovesLogSorterBinChangeProduct: 'Když zásobník třídiče kulatiny změní produkt',
    reverseDetection: 'Detekce couvání',
    reverseDetectionDescription: 'Detekuje, když vozidlo couvá, a podle toho upravuje jeho směr.',
  },
  addLogSorterDialog: {
    addLogSorter: 'Přidat třídič kulatiny',
    editLogSorter: 'Upravit třídič kulatiny',
    name: 'Název',
    bin: 'Zásobník',
    bins: 'Zásobníky',
    identifiers: 'ID',
    logSorter: 'Třídič kulatiny',
    selectLogSorter: 'Vybrat třídič kulatiny',
    startPositionX: 'Počáteční poloha X',
    startPositionY: 'Počáteční poloha Y',
    columns: 'Sloupce',
    numberOfBins: 'Počet zásobníků',
    numberSequence: 'Číselná řada',
    rotation: 'Otočení',
    direction: 'Směr',
    binWidth: 'Šířka zásobníku',
    binLength: 'Výška zásobníku',
    distanceX: 'Vzdálenost mezi osami X',
    distanceY: 'Vzdálenost mezi osami Y',
    geoFenceOffsetTop: 'Horní hranice geofence',
    geoFenceOffsetBottom: 'Dolní hranice geofence',
    geoFenceOffsetLeft: 'Levá hranice geofence',
    geoFenceOffsetRight: 'Pravá hranice geofence',
    geoFenceOffsetInside: 'Vnitřní hranice geofence',
    geoFenceOffsetOutside: 'Vnější hranice geofence',
    addLogWhenSorted: 'Přidat kulatinu po roztřídění',
    intake: 'Příjem',
    intakes: 'Příjmy',
    addNewIntake: 'Přidat nový příjem',
    deleteIntake: 'Odstranit příjem',
    savingStorageAreas: 'Ukládání skladovacích ploch',
    savingShapes: 'Ukládání tvarů',
    savingLogSorter: 'Ukládání třídiče kulatiny',
    success: 'Úspěšně!',
    savingBins: 'Ukládání zásobníků',
    savingIntakes: 'Ukládání příjmů',
    one: '1',
    twoLeft: '2L',
    twoRight: '2R',
  },
  dialogs: {
    storageArea: {
      addStorageArea: 'Vytvořit skladovací plochu',
      editStorageArea: 'Upravit skladovací plochu',
      storageArea: 'Skladovací plocha',
      automaticProductChange: 'Automatická změna produktu',
      allowMultipleProducts: 'Povolit více produktů',
      allowedProducts: 'Povolené produkty',
      selectAllowedProducts: 'Vybrat povolené produkty',
      selectStorageAreaType: 'Vybrat typ skladovací plochy',
      selectStorageAreaStatus: 'Vybrat stav skladovací plochy',
      confirmDeleteHeader: 'Odstranit skladovací prostor',
      confirmDeleteMessage: (name: string) =>
        `Are you sure you want to delete the storage area: ${name}?`,
      deleteError: 'Skladovací prostor se nepodařilo odstranit',
      updateError: 'Skladovací prostor se nepodařilo aktualizovat',
      createError: 'Skladovací prostor se nepodařilo vytvořit',
      editShape: 'Upravit tvar',
      sideArea: 'Boční plocha',
      sideAreaDescription:
        'Slouží k odhadu množství kmenů, které se vejde do tohoto skladovacího prostoru',
    },
    storageAreaSelect: {
      title: 'Informace o skladovacím prostoru',
    },
    inventory: {
      title: 'Skladové zásoby',
      selectProduct: 'Vybrat produkt',
      currentAmount: 'Aktuální množství:',
      setNewAmount: 'Nastavit nové množství',
      storageAreaNotFound: 'Skladovací prostor nebyl nalezen!',
      confirmHeader: (name: string) => `Inventory ${name}`,
      confirmMessage: (name: string, product: string) =>
        `Are you sure you want to add ${product} to ${name}? Allow multiple products is disabled.`,
    },
    confirmEmpty: {
      message:
        'Ve skladovacím prostoru je stále obsah. Opravdu jej chcete vyprázdnit? Zbývající obsah bude odstraněn.',
      title: (name: string) => `Empty storage area ${name}`,
      willBeDeleted: 'bude smazáno',
    },
    confirmAllowMultipleProducts: {
      message: (storageAreaName: string, product: string) =>
        `Allowed multiple products is disabled. Are you sure you wanna place "${product}" in ${storageAreaName}`,
      title: 'Varování',
    },
    confirmPlaceNotAllowedProductInLogYard: {
      message: (storageAreaName: string, products: string) =>
        `The product "${products}" are not allowed in ${storageAreaName}. Are you sure you want to place it here?`,
      title: 'Varování',
    },
    moveLogYardContent: {
      pickTab: 'Vyzvednout z',
      pickButton: 'Vyzvednout',
      placeTab: 'Umístit do',
      placeButton: 'Umístit',
      pickAll: 'Vyprázdnit',
    },
    selectVehicle: {
      title: 'Vyberte vozidlo',
      message: 'Vyberte prosím vozidlo, které používáte',
    },
    vehicleInventory: {
      vehicleInventory: 'Inventář vozidla',
      vehicleLoad: 'Aktuální náklad vozidla',
      leaveTitle: 'Nechat v',
      leaveButton: 'Nechat',
      moving: 'Přesun',
    },
    search: {
      searchForProduct: 'Vyhledat produkt',
      selectAProduct: 'Vyberte produkt',
    },
    mapView: {
      title: 'Přidat nový pohled na mapu',
      enterName: 'Zadejte název',
    },
    selectLogSorter: {
      title: 'Vyberte třídič kulatiny',
    },
    productionOrder: {
      editTitle: 'Vyberte výrobní zakázku',
      selectProductionOrder: 'Vyberte výrobní zakázku',
    },
  },
  error: {
    required: 'Povinné.',
    invalidValue: 'Neplatná hodnota.',
    nameAlreadyExist: 'Název již existuje.',
    nameIsRequired: 'Název je povinný.',
    maxLength: (limit: number) => `Max length ${limit}`,
    forbidden: 'Zakázáno',
    notFound: 'Nenalezeno',
    conflict: 'Konflikt',
    network: 'Nelze se připojit k serveru',
    invalidDensity: 'Hustota musí být alespoň 0,1',
  },
  enums: {
    storageAreaStatus(status: StorageAreaStatus): string {
      switch (status) {
        case StorageAreaStatus.Empty:
          return 'Prázdné';
        case StorageAreaStatus.InUse:
          return 'V použití';
        case StorageAreaStatus.Full:
          return 'Plné';
        case StorageAreaStatus.Undefined:
          return 'Nedefinováno';
      }
    },
    storageAreaType(type: StorageAreaType): string {
      switch (type) {
        case StorageAreaType.Producer:
          return 'Výrobce';
        case StorageAreaType.Consumer:
          return 'Spotřebitel';
        case StorageAreaType.Vehicle:
          return 'Vozidlo';
        case StorageAreaType.LogSorterIntake:
          return 'Vstup do třídiče kulatiny';
        case StorageAreaType.SawLineIntake:
          return 'Vstup do pily';
        case StorageAreaType.LogSorterBin:
          return 'Zásobník třídiče kulatiny';
        case StorageAreaType.SortedPile:
          return 'Tříděná hromada';
        case StorageAreaType.UnsortedPile:
          return 'Netříděná hromada';
        case StorageAreaType.Truck:
          return 'Nákladní auto';
        case StorageAreaType.Train:
          return 'Vlak';
        case StorageAreaType.Undefined:
          return 'Nedefinováno';
        case StorageAreaType.CoilStack:
          return 'Stoh svitků';
        case StorageAreaType.Bulk:
          return 'Sypký materiál';
        case StorageAreaType.PackageStorage:
          return 'Sklad balíků';
        case StorageAreaType.ProductionLine:
          return 'Výrobní linka';
        case StorageAreaType.PackageOutput:
          return 'Výstup balíků';
        case StorageAreaType.PackageIntake:
          return 'Příjem balíků';
      }
    },
    shapeMode(type: ShapeMode): string {
      switch (type) {
        case ShapeMode.Rectangle:
          return 'Obdélník';
        case ShapeMode.Circle:
          return 'Kruh';
        case ShapeMode.Polygon:
          return 'Mnohoúhelník';
      }
    },
    logSorterNumberSequence(value: LogSorterNumberSequence): string {
      switch (value) {
        case LogSorterNumberSequence.Sequential:
          return 'Sekvenční';
        case LogSorterNumberSequence.SequentialMirrored:
          return 'Sekvenční zrcadlené';
        case LogSorterNumberSequence.Alternating:
          return 'Střídavé';
        case LogSorterNumberSequence.AlternatingMirrored:
          return 'Střídavé zrcadlené';
      }
    },
    logSorterBinLayout(value: LogSorterColumnPlacement): string {
      switch (value) {
        case LogSorterColumnPlacement.Left:
          return 'Vlevo';
        case LogSorterColumnPlacement.Right:
          return 'Vpravo';
      }
    },
    anchor(value: Anchor): string {
      switch (value) {
        case Anchor.Center:
          return 'Uprostřed';
        case Anchor.BottomCenter:
          return 'Dole uprostřed';
        case Anchor.BottomLeft:
          return 'Dole vlevo';
        case Anchor.BottomRight:
          return 'Dole vpravo';
        case Anchor.TopCenter:
          return 'Nahoře uprostřed';
        case Anchor.TopLeft:
          return 'Nahoře vlevo';
        case Anchor.TopRight:
          return 'Nahoře vpravo';
      }
    },
    auditTrailAction(action: AuditTrailAction): string {
      switch (action) {
        case AuditTrailAction.InventoryApiExecute:
          return 'Skladové zásoby';
        case AuditTrailAction.InventoryApiUndo:
          return 'Zrušit skladové zásoby';
        case AuditTrailAction.LogSorterApiCreate:
          return 'Vytvořit třídič kulatiny';
        case AuditTrailAction.LogSorterApiDelete:
          return 'Odstranit třídič kulatiny';
        case AuditTrailAction.LogSorterApiUpdate:
          return 'Aktualizovat třídič kulatiny';
        case AuditTrailAction.MapStyleApiCreate:
          return 'Vytvořit styl mapy';
        case AuditTrailAction.MapStyleApiDelete:
          return 'Odstranit styl mapy';
        case AuditTrailAction.MapStyleApiUpdate:
          return 'Aktualizovat styl mapy';
        case AuditTrailAction.MoveApiUndo:
          return 'Zrušit přesun';
        case AuditTrailAction.MoveApi:
          return 'Přesunout';
        case AuditTrailAction.ProductApiCreate:
          return 'Vytvořit produkt';
        case AuditTrailAction.ProductApiDelete:
          return 'Odstranit produkt';
        case AuditTrailAction.ProductApiUpdate:
          return 'Aktualizovat produkt';
        case AuditTrailAction.PackageApiCreate:
          return 'Vytvořit balík';
        case AuditTrailAction.PackageApiUpdate:
          return 'Aktualizovat balík';
        case AuditTrailAction.PackageApiDelete:
          return 'Odstranit balík';
        case AuditTrailAction.PackagingApiCreate:
          return 'Vytvořit balení';
        case AuditTrailAction.PackagingApiUpdate:
          return 'Aktualizovat balení';
        case AuditTrailAction.PackagingApiDelete:
          return 'Odstranit balení';
        case AuditTrailAction.PackageTypeApiCreate:
          return 'Vytvořit typ balíku';
        case AuditTrailAction.PackageTypeApiUpdate:
          return 'Aktualizovat typ balíku';
        case AuditTrailAction.PackageTypeApiDelete:
          return 'Odstranit typ balíku';
        case AuditTrailAction.SyncPackage:
          return 'Aktualizace balíku';
        case AuditTrailAction.ShapeApiCreate:
          return 'Vytvořit tvar';
        case AuditTrailAction.ShapeApiDelete:
          return 'Odstranit tvar';
        case AuditTrailAction.ShapeApiUpdate:
          return 'Aktualizovat tvar';
        case AuditTrailAction.ShapeApiUndoDelete:
          return 'Zrušit aktualizaci tvaru';
        case AuditTrailAction.StorageAreaApiCreate:
          return 'Vytvořit skladovací prostor';
        case AuditTrailAction.StorageAreaApiDelete:
          return 'Odstranit skladovací prostor';
        case AuditTrailAction.StorageAreaApiUndoDelete:
          return 'Zrušit odstranění skladovacího prostoru';
        case AuditTrailAction.StorageAreaApiUpdate:
          return 'Aktualizovat skladovací prostor';
        case AuditTrailAction.SyncMove:
          return 'Přesunout';
        case AuditTrailAction.VehicleApiCreate:
          return 'Vytvořit vozidlo';
        case AuditTrailAction.VehicleApiDelete:
          return 'Odstranit vozidlo';
        case AuditTrailAction.VehicleApiUpdate:
          return 'Aktualizovat vozidlo';
        case AuditTrailAction.Undefined:
          return 'Nedefinováno';
        case AuditTrailAction.SyncInventory:
          return 'Synchronizovat zásoby';
        case AuditTrailAction.SyncProduct:
          return 'Synchronizovat produkt';
        case AuditTrailAction.SyncShape:
          return 'Synchronizovat tvar';
        case AuditTrailAction.SyncStorageArea:
          return 'Synchronizovat skladovací plochu';
        case AuditTrailAction.SyncUndoInventory:
          return 'Synchronizovat vrácení zpět zásob';
        case AuditTrailAction.SyncUndoMove:
          return 'Synchronizovat vrácení zpět přesunu';
        case AuditTrailAction.SyncUndoStorageArea:
          return 'Synchronizovat vrácení zpět skladovací plochy';
        case AuditTrailAction.CoilApiCreate:
          return 'Vytvořit svitek';
        case AuditTrailAction.CoilApiUpdate:
          return 'Aktualizovat svitek';
        case AuditTrailAction.CoilApiDelete:
          return 'Odstranit svitek';
        case AuditTrailAction.FeatureFlagApi:
          return 'Aktualizovat příznaky funkcí';
        case AuditTrailAction.SettingUpdate:
          return 'Aktualizovat nastavení';
        case AuditTrailAction.SyncFeatureFlag:
          return 'Synchronizovat příznaky funkcí';
        case AuditTrailAction.SyncLogSorter:
          return 'Synchronizovat třídič kulatiny';
        case AuditTrailAction.SyncMapStyle:
          return 'Synchronizovat styly mapy';
        case AuditTrailAction.SyncSetting:
          return 'Synchronizovat nastavení';
        case AuditTrailAction.SyncVehicle:
          return 'Synchronizovat vozidla';
        case AuditTrailAction.ProductGroupApiCreate:
          return 'Vytvořit skupinu produktů';
        case AuditTrailAction.ProductGroupApiUpdate:
          return 'Aktualizovat skupinu produktů';
        case AuditTrailAction.ProductGroupApiDelete:
          return 'Odstranit skupinu produktů';
        case AuditTrailAction.ProductGroupSync:
          return 'Synchronizovat skupinu produktů';
        case AuditTrailAction.DashboardCreate:
          return 'Vytvořit řídicí panel';
        case AuditTrailAction.DashboardUpdate:
          return 'Aktualizovat řídicí panel';
        case AuditTrailAction.DashboardDelete:
          return 'Odstranit řídicí panel';
        case AuditTrailAction.MoveTimberFromLogSorterBins:
          return 'Přesunout dřevo ze zásobníků třídiče kmenů';
        case AuditTrailAction.ProductionOrderCreate:
          return 'Vytvořit výrobní zakázku';
        case AuditTrailAction.ProductionOrderUpdate:
          return 'Aktualizovat výrobní zakázku';
        case AuditTrailAction.ProductionOrderDelete:
          return 'Odstranit výrobní zakázku';
        case AuditTrailAction.ProductionOrderSync:
          return 'Synchronizovat výrobní zakázku';
        case AuditTrailAction.ProductionOrderChange:
          return 'Změnit výrobní zakázku';
        case AuditTrailAction.InboundDeliveryCreate:
          return 'Vytvořit příchozí dodávku';
        case AuditTrailAction.InboundLoadCreate:
          return 'Vytvořit příchozí náklad';
        case AuditTrailAction.SyncInboundLoad:
          return 'Synchronizovat příchozí náklad';
        case AuditTrailAction.SyncInboundDelivery:
          return 'Synchronizovat příchozí dodávku';
        case AuditTrailAction.InboundDeliveryDelete:
          return 'Odstranit příchozí dodávku';
        case AuditTrailAction.InboundDeliveryUpdate:
          return 'Aktualizovat příchozí dodávku';
        default:
          return 'Neznámá akce';
      }
    },
    dashboardComponentType(type: DashboardComponentType): string {
      switch (type) {
        case DashboardComponentType.StorageAreaTable:
          return 'Tabulka skladového prostoru';
        case DashboardComponentType.LatestReleaseNotes:
          return 'Nejnovější poznámky k verzi';
        case DashboardComponentType.ProductGroupDiagram:
          return 'Diagram skupin produktů';
        case DashboardComponentType.ProductGroupTable:
          return 'Tabulka skupin produktů';
        case DashboardComponentType.ProductTable:
          return 'Tabulka produktů';
        case DashboardComponentType.AsljungapallenLagersaldo:
          return 'Åsljungapallen lagersaldo';
        default:
          return 'Neznámý typ komponenty řídicího panelu';
      }
    },
    keyboardAction(action: KeyboardActions): string {
      switch (action) {
        case KeyboardActions.DropOff:
          return 'Vysazení';
        case KeyboardActions.PickUp:
          return 'Naložení';
        case KeyboardActions.ZoomIn:
          return 'Přiblížit';
        case KeyboardActions.ZoomOut:
          return 'Oddálit';
        case KeyboardActions.ChangeMapStyle:
          return 'Změnit styl mapy';
        case KeyboardActions.OpenInventory:
          return 'Otevřít/zavřít inventář';
        case KeyboardActions.RotateLeft:
          return 'Otočit doleva';
        case KeyboardActions.RotateRight:
          return 'Otočit doprava';
        case KeyboardActions.ToggleStorageAreaFull:
          return 'Přepnout skladovací prostor na „plný“ / „v použití“';
        case KeyboardActions.OpenStorageArea:
          return 'Otevřít/zavřít skladovací prostor';
        case KeyboardActions.Undo:
          return 'Zpět';
        case KeyboardActions.CreateStorageArea:
          return 'Vytvořit skladovací prostor';
        case KeyboardActions.SelectNextNearbyStorageArea:
          return 'Vybrat další blízký skladovací prostor';
        case KeyboardActions.SelectPreviousNearbyStorageArea:
          return 'Vybrat předchozí blízký skladovací prostor';
      }
    },
    productionOrderState(state: ProductionOrderState): string {
      switch (state) {
        case ProductionOrderState.Deleted:
          return 'Smazáno';
        case ProductionOrderState.Planned:
          return 'Plánováno';
        case ProductionOrderState.Released:
          return 'Uvolněno';
        case ProductionOrderState.Reserved:
          return 'Rezervováno';
        case ProductionOrderState.Preparing:
          return 'Příprava';
        case ProductionOrderState.Prepared:
          return 'Připraveno';
        case ProductionOrderState.Delivering:
          return 'Doručování';
        case ProductionOrderState.Completed:
          return 'Dokončeno';
        case ProductionOrderState.Registered:
          return 'Zaregistrováno';
        case ProductionOrderState.PreparingPaused:
          return 'Příprava pozastavena';
        case ProductionOrderState.DeliveringPaused:
          return 'Doručování pozastaveno';
      }

      return 'Neznámé';
    },
    deliveryStatus(status: DeliveryStatus): string {
      switch (status) {
        case DeliveryStatus.Planned:
          return 'Plánováno';
        case DeliveryStatus.Released:
          return 'Uvolněno';
        case DeliveryStatus.Active:
          return 'Aktivní';
        case DeliveryStatus.Finished:
          return 'Dokončeno';
        case DeliveryStatus.Invoiced:
          return 'Fakturováno';
      }

      return 'Neznámé';
    },
  },
  reports: {
    exportFormat: {
      pdf: 'PDF',
      excel: 'Excel',
    },
    reportName(report: ReportType): string {
      switch (report) {
        case ReportType.LogYardStatus:
          return 'Zpráva o stavu';
        case ReportType.LogYardPeriod:
          return 'Zpráva za období';
        case ReportType.LogYardVehicleMoves:
          return 'Pohyby vozidel';
        case ReportType.LogYardMovedProducts:
          return 'Přemístěné produkty';
        case ReportType.LogYardVehicleStatistics:
          return 'Statistiky vozidel';
        default:
          return 'Neznámá zpráva';
      }
    },
  },
  storageAreas: {
    actions: {
      create: {
        title: 'Vytvořit skladovací prostor',
        pending: 'Vytváření skladovacího prostoru',
        error: 'Vytvoření skladovacího prostoru se nezdařilo',
      },
      fetch: {
        title: 'Načítání skladovacího prostoru',
        error: 'Načítání skladovacího prostoru se nezdařilo',
      },
      fetchMany: {
        title: 'Načítání skladovacích prostorů',
        error: 'Načítání skladovacích prostorů se nezdařilo',
      },
      inventory: {
        title: 'Inventarizace skladovacího prostoru',
        error: 'Inventarizace skladovacího prostoru se nezdařila',
      },
      undoInventory: {
        title: 'Zrušit inventarizaci',
        error: 'Zrušení inventarizace se nezdařilo',
      },
      delete: {
        title: 'Odstranění skladovacího prostoru',
        error: 'Vymazání skladového prostoru se nezdařilo',
      },
      update: {
        title: 'Aktualizace skladového prostoru',
        error: 'Aktualizace skladového prostoru se nezdařila',
      },
      undoDelete: {
        title: 'Zrušení vymazání skladového prostoru',
        error: 'Zrušení vymazání skladového prostoru se nezdařilo',
      },
    },
    copy: 'Kopírování',
    notFound: 'Skladový prostor nebyl nalezen',
    storageAreaTable: {
      firstMoveTo: 'První přesun',
      daysSinceFirstMove: 'Počet dní od prvního přesunu',
    },
  },
  productionOrders: {
    actions: {
      fetchMany: {
        title: 'Načítání výrobních zakázek',
        error: 'Načítání výrobních zakázek se nezdařilo',
      },
      fetch: {
        title: 'Načítání výrobní zakázky',
        error: 'Načítání výrobní zakázky se nezdařilo',
      },
      create: {
        title: 'Vytváření výrobní zakázky',
        error: 'Vytvoření výrobní zakázky se nezdařilo',
      },
      update: {
        title: 'Aktualizace výrobní zakázky',
        error: 'Aktualizace výrobní zakázky se nezdařila',
      },
      delete: {
        title: 'Odstranění výrobního příkazu',
        error: 'Odstranění výrobního příkazu se nezdařilo',
        statusCodes: {
          409: 'Odstranění výrobního příkazu se nezdařilo',
        },
      },
      change: {
        title: 'Změna výrobního příkazu',
        error: 'Změna výrobního příkazu se nezdařila',
      },
    },
    listPage: {
      title: 'Výrobní příkazy',
      noFound: 'Nebyly nalezeny žádné výrobní příkazy',
      loading: 'Načítání výrobních příkazů...',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete production order ${name}`;
      },
      description: 'Opravdu chcete tento výrobní příkaz odstranit? Tuto akci nelze vrátit zpět.',
      deleteButton: 'Ano, odstranit výrobní příkaz',
    },
    editPage: {
      editTitle: 'Upravit výrobní příkaz',
      createTitle: 'Vytvořit výrobní příkaz',
      productionInstruction: 'Výrobní pokyny',
      vehicleInstruction: 'Pokyny pro vozidlo',
      state: 'Stav',
      recipeInstruction: 'Pokyny',
      recipeMachineInstruction: 'Pokyny pro stroj',
      consumeEntireStock: 'Spotřebovat celou zásobu',
      noProducts: 'Žádné produkty',
      inProductsTitle: 'Příchozí produkty',
      outProductsTitle: 'Odchozí produkty',
      editRecipeTitle: 'Upravit recept',
      addRecipeTitle: 'Přidat recept',
      addRecipeButton: 'Přidat recept',
      recipesTitle: 'Recepty',
    },
    plannedStart: 'Plánované zahájení',
  },
  shapes: {
    name: (id: string) => `Shape #${id}`,
    multipleNames: (number: number) => `Edit (${number}) shapes`,
    actions: {
      fetchMany: {
        title: 'Načítání tvarů',
        error: 'Načtení tvarů se nezdařilo',
      },
      fetch: {
        title: 'Načítání tvaru',
        error: 'Načtení tvaru se nezdařilo',
      },
      update: {
        title: 'Aktualizace tvaru',
        error: 'Aktualizace tvaru se nezdařila',
      },
      create: {
        title: 'Vytvořit tvar',
        pending: 'Vytváření tvaru',
        error: 'Vytvoření tvaru se nezdařilo',
      },
      delete: {
        title: 'Odstranění tvaru',
        error: 'Odstranění tvaru se nezdařilo',
      },
      query: {
        title: 'Dotaz na tvary',
        error: 'Dotaz na tvary se nezdařil',
      },
      undoDelete: {
        title: 'Zrušit smazání tvaru',
        error: 'Zrušení smazání tvaru se nezdařilo',
      },
    },
    geofenceFieldSet: 'Geofence',
    zIndex: 'Z-index',
  },
  settings: {
    actions: {
      read: {
        title: 'Načíst nastavení',
        error: 'Načtení nastavení se nezdařilo',
      },
      save: {
        title: 'Uložit nastavení',
        error: 'Uložení nastavení se nezdařilo',
      },
    },
    general: {
      title: 'Obecné',
      language: 'Jazyk',
      dark: 'Tmavý',
      light: 'Světlý',
      colorMode: 'Barevný režim',
      scale: 'Velikost uživatelského rozhraní',
      auto: 'Automaticky',
      mapColorMode: 'Barevný režim mapy',
    },
    map: {
      title: 'Mapa',
      style: 'Styl',
      coordinate: 'Souřadnice',
      centerMapOnVehicle: 'Vycentrovat mapu na vozidlo',
      followVehicleHeading: 'Otáčet mapu podle směru jízdy',
      snapPlacementToPlace: 'Přichytit odložený balíček k úložnému místu',
      showPackagePanel: 'Zobrazit panel balíčků',
    },
    keybinds: {
      title: 'Klávesové zkratky',
    },
    about: {
      title: 'O aplikaci',
    },
    vehicle: {
      title: 'Vozidlo',
      activeVehicle: 'Aktivní vozidlo',
      speedLimit: 'Rychlostní limit 5 km/h pro aktivaci úložného prostoru',
    },
  },
  featureFlags: {
    actions: {
      read: {
        title: 'Načíst příznaky funkcí',
        error: 'Načtení příznaků funkcí se nezdařilo',
      },
      refresh: {
        title: 'Obnovit příznaky funkcí',
        error: 'Obnovení příznaků funkcí se nezdařilo',
      },
      toggle: {
        title: 'Přepnout příznaky funkcí',
        error: 'Přepnutí příznaků funkcí se nezdařilo',
      },
    },
    refresh: {
      success: 'Příznaky funkcí byly úspěšně obnoveny',
    },
    toggle: {
      success: 'Příznak funkce byl úspěšně přepnut',
    },
    component: {
      title: 'Příznaky funkcí',
      description: 'Povolení nebo zakázání funkcí v aplikaci',
      refreshButton: 'Obnovit příznaky funkcí',
    },
  },
  auth: {
    loggedOut: {
      title: 'Jste odhlášeni!',
      description: 'Abyste mohli pokračovat v práci, musíte se přihlásit.',
    },
    unauthorized: {
      title: 'Váš uživatel nemá oprávnění k používání této aplikace',
      description: 'Musíte se obrátit na správce, aby vám udělil oprávnění k jejímu používání.',
    },
  },
  map: {
    contextmenu: {
      create: {
        title: 'Vytvořit',
        shape: 'Tvar',
        storageArea: 'Skladovací oblast',
        logSorter: 'Třídič protokolů',
        intake: 'Příjem',
        productionLine: 'Výrobní linka',
        insideProductionLine: 'Vytvořit uvnitř výrobní linky',
        packageOutput: 'Výstup balíků',
        packageIntake: 'Příjem balíků',
      },
      edit: 'Upravit',
      delete: 'Odstranit',
      audit: 'Audit',
      copy: 'Kopírovat',
      move: 'Přesunout',
      addInboundDeliveryTransaction: 'Přidat příchozí transakci',
      addOutboundDeliveryTransaction: 'Přidat transakci odchozí dodávky',
      spawnPackage: 'Vytvořit balík',
      copyPackage: 'Kopírovat balík',
    },
    actions: {
      saveMapMode: {
        title: 'Uložit režim mapy',
        error: 'Nepodařilo se uložit režim mapy',
      },
    },
  },
  productionLine: {
    simulation: 'Simulace',
    areas: 'Oblasti',
    addOutputArea: 'Přidat výstupní oblast',
    addIntakeArea: 'Přidat vstupní oblast',
    saveLineFirst: 'Před přidáním výstupních nebo vstupních oblastí uložte výrobní linku.',
    simulationEnabled: 'Simulace povolena',
    intervalSeconds: 'Interval (v sekundách)',
    outputPlace: 'Místo výstupu',
    product: 'Produkt',
    piecesCount: 'Počet kusů',
    packaging: 'Balení',
    packageType: 'Typ balení',
    length: 'Délka',
    width: 'Šířka',
    height: 'Výška',
  },
  logSorter: {
    actions: {
      query: {
        title: 'Dotaz na třídič kmenů',
        error: 'Nepodařilo se provést dotaz na třídič kmenů',
      },
      update: {
        title: 'Aktualizace třídiče kmenů',
        error: 'Nepodařilo se aktualizovat třídič kmenů',
      },
      create: {
        title: 'Vytváření třídiče kmenů',
        error: 'Vytvoření třídiče kmenů se nezdařilo',
      },
      delete: {
        title: 'Odstranění třídiče kmenů',
        error: 'Odstranění třídiče kmenů se nezdařilo',
      },
      fetch: {
        title: 'Načtení třídiče kmenů',
        error: 'Načtení třídiče kmenů se nezdařilo',
      },
    },
    drawer: {
      createTitle: 'Vytvořit třídič kmenů',
      binName: (binNumber: number) => `Bin ${binNumber}`,
      logSorterTab: 'Třídič kmenů',
      binsTab: 'Zásobníky',
    },
    deleteConfirm: {
      title: `Delete log sorter`,
      message: (name: string) => `Are you sure you want to delete the log sorter ${name}?`,
    },
    panel: {
      identifier: 'Identifikátor',
      numberSequence: 'Číselná řada',
      numberOfBinsCol1: 'Počet zásobníků v prvním sloupci',
      numberOfBinsCol2: 'Počet zásobníků ve druhém sloupci',
      binLayout: 'Druhý sloupec do',
      binWidth: 'Šířka zásobníku',
      binLength: 'Délka zásobníku',
      columnGap: 'Mezery mezi sloupci',
      rowGap: 'Mezery mezi řádky',
      intakes: 'Příjmy',
      offsetY: 'Posun Y',
      offsetX: 'Posun X',
    },
  },
  mapStyles: {
    actions: {
      create: {
        title: 'Vytváření stylu',
        error: 'Vytvoření stylu se nezdařilo',
      },
      update: {
        title: 'Aktualizace stylu',
        error: 'Aktualizace stylu se nezdařila',
      },
      delete: {
        title: 'Odstranění stylu',
        error: 'Odstranění stylu se nezdařilo',
      },
      fetch: {
        title: 'Načítání stylu',
        error: 'Načítání stylu se nezdařilo',
      },
      fetchMany: {
        title: 'Načítání stylů',
        error: 'Načítání stylů se nezdařilo',
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Delete style ${name}`;
      },
      description: 'Opravdu chcete tento styl odstranit? Tuto akci nelze vrátit zpět.',
      deleteButton: 'Ano, smazat styl',
    },

    editDialog: {
      create: {
        title: 'Vytvořit nový styl',
      },
      edit: {
        title: 'Upravit styl',
      },
      tip: 'Stisknutím kláves Ctrl+mezerník otevřete informace o dostupných funkcích',
      documentation: 'Další informace o vzorcích najdete v',
      documentationLinkText: 'dokumentaci ke stylům map',
      shapeSet: 'Tvar',
      vehicleSet: 'Vozidlo',
      labelFormula: 'Popisek',
      colorFormula: 'Barva',
      outlineColorFormula: 'Barva obrysu',
      outlineSizeFormula: 'Velikost obrysu',
      fontSizeFormula: 'Velikost písma',
      messageIconFormula: 'Zobrazit ikonu zprávy',
      placeSet: 'Úložná místa',
      placeVisibleFormula: 'Viditelné',
      placeLabelFormula: 'Popisek',
      placeOutlineColorFormula: 'Barva obrysu',
      placeOutlineSizeFormula: 'Velikost obrysu',
      placeAccessibleOutlineColorFormula: 'Barva obrysu přístupné strany',
      placeInaccessibleOutlineColorFormula: 'Barva obrysu nepřístupné strany',
      placeTextAlignmentFormula: 'Zarovnání textu (vlevo, na střed, vpravo)',
      placeTextPositionFormula: 'Poloha textu (na střed, nahoře, dole)',
      placeTextOpacityFormula: 'Průhlednost textu (0–100)',
      placeTextColorFormula: 'Barva textu',
      placeTextSizeFormula: 'Velikost textu',
      placeBackgroundColorFormula: 'Barva pozadí',
      placeBackgroundPatternFormula: 'Vzor pozadí (plná, šrafovaná, křížky, tečky)',
      placeBackgroundOpacityFormula: 'Průhlednost pozadí (0–100)',
      packageSet: 'Balíčky',
      packageVisibleFormula: 'Viditelné',
      packageLabelFormula: 'Štítek',
      packageOutlineColorFormula: 'Barva obrysu',
      packageOutlineSizeFormula: 'Velikost obrysu',
      packageTextAlignmentFormula: 'Zarovnání textu (vlevo, na střed, vpravo)',
      packageTextPositionFormula: 'Poloha textu (uprostřed, nahoře, dole)',
      packageTextOpacityFormula: 'Průhlednost textu (0–100)',
      packageTextColorFormula: 'Barva textu',
      packageTextSizeFormula: 'Velikost textu',
      packageBackgroundColorFormula: 'Barva pozadí',
      packageBackgroundPatternFormula: 'Vzor pozadí (plná, šrafovaná, křížky, tečky)',
      packageBackgroundOpacityFormula: 'Průhlednost pozadí (0–100)',
      proximitySet: 'Blízkost',
      proximityVisibleFormula: 'Viditelné',
      proximityAreaColorFormula: 'Barva oblasti',
      proximityConeColorFormula: 'Barva kužele přístupnosti',
      colorPicker: 'Vybírač barev (slouží k zobrazení a výběru barevných kódů)',
    },

    page: {
      title: 'Styly map',
      notFound: 'Nebyly nalezeny žádné styly map',
      loading: 'Načítání stylů map',
      copiedToClipboard: 'Zkopírováno do schránky!',
      noStyleInClipboard: 'Ve schránce není žádný styl',
      noStyleInClipboardDescription: 'Před importem je nutné styl exportovat do schránky.',
      importFromClipboardTooltip: 'Importovat ze schránky',
      failedToImport: 'Import ze schránky se nezdařil:',
      clipBoardAccessDeniedSummary: 'Přístup ke schránce byl odepřen',
      clipBoardAccessDeniedDetail: 'Povolte této webové stránce přístup ke schránce a zkuste to znovu.'
    },
  },
  systemSettings: {
    page: {
      title: 'Systémová nastavení',
      settingsSaved: 'Nastavení byla úspěšně uložena',
      storagearea: {
        title: 'Úložná oblast',
        nameFormat: 'Formát názvu',
        nameFormatRequired: 'Formát názvu je povinný',
        nameFormatMissingSequence: 'Formát názvu musí obsahovat zástupný symbol {#} pro pořadí',
        displayText: 'Zobrazovací text',
        displayTextFormula: 'Vzorec zobrazovacího textu',
        displayTextFormulaTitle: 'Konfigurovat zobrazovací text',
        displayTextFormulaDescription:
          'Tento vzorec se používá k výpočtu zobrazovaného textu pro každou skladovací plochu. Používá stejné proměnné a funkce jako vzorce tvarů stylů mapy.',
        configureDisplayText: 'Nakonfigurovat vzorec zobrazovaného textu',
        placeDisplayText: 'Umístění zobrazovaného textu',
        placeDisplayTextFormula: 'Vzorec umístění zobrazovaného textu',
        placeDisplayTextFormulaTitle: 'Nakonfigurovat umístění zobrazovaného textu',
        placeDisplayTextFormulaDescription:
          'Tento vzorec se používá k výpočtu zobrazovaného textu pro každé skladovací místo. Používá stejné proměnné a funkce jako vzorce míst stylů mapy.',
        configurePlaceDisplayText: 'Nakonfigurovat vzorec umístění zobrazovaného textu',
        logYardLength: 'Délka skladovacího prostoru',
        logYardWidth: 'Šířka skladovacího prostoru',
        logYardSideArea: 'Plocha strany skladovacího prostoru',
        logYard: 'Sklad dřeva',
      },
      featureflags: {
        title: 'Příznaky prvků',
      },
      reports: {
        title: 'Zprávy',
        defaultReports: 'Výchozí zprávy',
        customerSpecificReports: 'Pouze pro konkrétního zákazníka',
      },
      map: {
        minZoom: 'Minimální úroveň přiblížení mapy (0–24)',
      },
      dashboards: {
        title: 'Komponenty řídicího panelu',
        defaultComponents: 'Výchozí komponenty',
        customerSpecificComponents: 'Pouze pro konkrétního zákazníka',
      },
    },
  },
  actions: {
    fetchVersion: {
      title: 'Načítání verze aplikace',
      error: 'Načtení verze aplikace se nezdařilo',
    },
  },
  undo: {
    undoBtn: 'Zpět',
    pickTitle: 'Zpět – akce výběru',
    getUndoPickDescription(
      oldStorageAreaCount: number,
      oldVehicleCount: number,
      storageAreaName: string,
    ): string {
      return `Confirm to remove ${oldVehicleCount} logs from your vehicle and restore storage area ${storageAreaName} to amount: ${oldStorageAreaCount}`;
    },
    getUndoPickBulkDescription(products: string, storageAreaName: string): string {
      return `Restore ${products} to ${storageAreaName} and remove it from your vehicle.`;
    },
    placeTitle: 'Zpět – akce umístění',
    getUndoPlaceDescription(numberOfLogs: number, storageAreaName: string): string {
      return `Confirm to restore ${numberOfLogs} logs to your vehicle and remove ${numberOfLogs} logs from storage area ${storageAreaName}`;
    },
    getUndoPlaceBulkDescription(products: string, storageAreaName: string): string {
      return `Undo placing ${products} into ${storageAreaName}. The bulk material will be removed from the storage area and restored to your vehicle.`;
    },
    inventoryTitle: 'Zpět – akce inventáře',
    getUndoInventoryDescription(
      productname: string,
      numberOfLogs: number,
      storageAreaName: string,
    ): string {
      return `Confirm to undo the latest inventory action on storageArea ${storageAreaName} and set amount of ${productname} back to ${numberOfLogs}`;
    },
    getUndoBulkInventoryDescription(
      productname: string,
      value: number,
      unit: Unit,
      storageAreaName: string,
    ): string {
      return `Confirm to undo the latest inventory action on storage area ${storageAreaName} and set amount of ${productname} back to ${value}`;
    },
    createStorageAreaTitle: 'Zpět – vytvoření skladovací plochy',
    getUndoCreateStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo create storage area ${storageAreaName}`;
    },
    deleteStorageAreaTitle: 'Zpět – odstranění skladovací plochy',
    getUndoDeleteStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo delete storage area ${storageAreaName}`;
    },
    editStorageAreaTitle: 'Zpět – úprava skladovací plochy',
    getUndoEditStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo edit on storage area ${storageAreaName}`;
    },
    createShape: {
      title: 'Zpět – vytvoření tvaru',
      description: `Confirm to undo create shape`,
    },
    updateShape: {
      title: 'Zpět – aktualizace tvaru',
      description: 'Potvrďte zrušení aktualizace tvaru',
    },
    deleteShape: {
      title: 'Zpět – smazání tvaru',
      description: 'Potvrďte zrušení smazání tvaru',
    },
  },
  quickMenu: {
    addStorageArea: 'Vytvořit skladovací plochu',
    editStorageArea: 'Upravit skladovací plochu',
  },
  moves: {
    actions: {
      moveTimberFromLogSorterBins: {
        title: 'Přesunout dřevo ze zásobníků třídiče kulatiny',
        error: 'Přesun dřeva ze zásobníků třídiče kulatiny se nezdařil',
      },
      pick: {
        title: 'Vyzvednutí ze skladovací plochy',
        error: 'Vyzvednutí ze skladovací plochy se nezdařilo',
      },
      place: {
        title: 'Umístit do skladovací plochy',
        error: 'Umístění ze skladovací plochy se nezdařilo',
      },
      pickAndPlace: {
        title: 'Vyzvednout a umístit',
        error: 'Vyzvednutí a umístění se nezdařilo',
      },
    },
    failedDialog: {
      title: 'Přesun se nezdařil!',
      description: (fromStorageArea: string, toStorageArea: string) =>
        `Move from ${fromStorageArea} to ${toStorageArea}`,
    },
    moveTimberFromLogSorterBins: 'Přesun dřeva ze zásobníků',
  },
  home: {
    welcome: 'Vítejte v',
    description: 'logistickém systému nové generace',
    username: 'Uživatelské jméno',
    enterUsername: 'Zadejte své uživatelské jméno pro přihlášení',
    rememberUsername: 'Zapamatovat uživatelské jméno',
    signInButton: 'Přihlásit se',
  },
  unsavedChanges: {
    title: 'Máte neuložené změny!',
    description: 'Co s nimi chcete udělat?',
  },
  audit: {
    dialog: {
      title: (name: string) => `Changes of ${name}`,
    },
    actions: {
      fetchShape: {
        title: 'Načítání auditu tvarů',
        error: 'Načítání auditu tvarů se nezdařilo',
      },
      fetchVehicle: {
        title: 'Načítání auditu vozidel',
        error: 'Načítání auditu vozidel se nezdařilo',
      },
      fetchProduct: {
        title: 'Načítání auditu produktů',
        error: 'Načítání auditu produktů se nezdařilo',
      },
      fetchPackage: {
        title: 'Načítání auditu balíků',
        error: 'Načítání auditu balíků se nezdařilo',
      },
      fetchPackaging: {
        title: 'Načítání auditu balení',
        error: 'Načítání auditu balení se nezdařilo',
      },
      fetchPackageType: {
        title: 'Načítání auditu typů balíků',
        error: 'Načítání auditu typů balíků se nezdařilo',
      },
      fetchProductGroup: {
        title: 'Načítání auditu skupiny produktů',
        error: 'Načítání auditu skupiny produktů se nezdařilo',
      },
      fetchDashboard: {
        title: 'Načítání auditu řídicího panelu',
        error: 'Načítání auditu řídicího panelu se nezdařilo',
      },
      fetchProductionOrder: {
        title: 'Načítání auditu výrobní zakázky',
        error: 'Načítání auditu výrobní zakázky se nezdařilo',
      },
      fetchInboundDelivery: {
        title: 'Načítání auditu příchozí dodávky',
        error: 'Načítání auditu příchozí dodávky se nezdařilo',
      },
      fetchOutboundDelivery: {
        title: 'Načítání auditu odchozí dodávky',
        error: 'Načítání auditu odchozí dodávky se nezdařilo',
      },
    },
  },
  packages: {
    actions: {
      fetchMany: {
        title: 'Načítání balíků',
        error: 'Načítání balíků se nezdařilo',
      },
      create: {
        title: 'Vytváření balíku',
        error: 'Vytvoření balíku se nezdařilo',
      },
      update: {
        title: 'Aktualizace balíku',
        error: 'Aktualizace balíku se nezdařila',
      },
      delete: {
        title: 'Odstranění balíku',
        error: 'Odstranění balíku se nezdařilo',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Delete package ${name}`,
      description: 'Opravdu chcete tento balík odstranit?',
      deleteButton: 'Ano, odstranit balík',
    },
    packages: 'Balíky',
    addNewPackage: 'Přidat balík',
    editPackage: 'Upravit balík',
    groups: {
      core: 'Základní',
      physical: 'Fyzické',
      nominal: 'Nominální',
      statistics: 'Statistiky',
    },
    layoutMode: 'Rozložení',
    layoutModes: {
      fieldset: 'Sady polí',
      accordion: 'Akordeon',
    },
    showDialogOptions: 'Zobrazit možnosti dialogového okna',
    hideDialogOptions: 'Skrýt možnosti dialogového okna',
    weightExceedsMaxWarning: 'Celková hmotnost překračuje maximální povolenou hmotnost pro jedno nebo více obsazených skladovacích míst.',
    heightExceedsMaxWarning: 'Celková výška překračuje maximální povolenou výšku pro jedno nebo více obsazených skladovacích míst.',
    stackLevelExceedsMaxWarning: 'Úroveň stohu překračuje maximální počet stohů pro jedno nebo více obsazených skladovacích míst.',
    placementOverlapWarning: 'Vybraná skladovací místa jsou na této úrovni stohu již obsazena jiným balíkem.',
    placesNotContiguousWarning: 'Obsazená skladovací místa nejsou souvislá.',
    occupiedPlacesNotContiguousWarning: 'Obsazená skladovací místa nejsou souvislá.',
    moveDialog: 'Přesunout dialogové okno',
    dragGroup: 'Přetáhnout',
    moveGroupUp: 'Posunout skupinu nahoru',
    moveGroupDown: 'Posunout skupinu dolů',
    resizeGroupHeight: 'Změnit výšku skupiny',
    resizeGroupWidth: 'Změnit šířku skupiny',
    fields: {
      number: 'Číslo',
      externalNumber: 'Externí číslo',
      packageStorageArea: 'Úložný prostor balíku',
      anchorStoragePlace: 'Úložné místo kotvy',
      occupiedStoragePlaces: 'Obsazená úložná místa',
      stackLevel: 'Úroveň stohu',
      accessibilityRating: 'Hodnocení přístupnosti',
      nominalWeight: 'Nominální hmotnost',
      weight: 'Hmotnost',
      nominalHeight: 'Nominální výška',
      height: 'Výška',
      nominalWidth: 'Nominální šířka',
      width: 'Šířka',
      nominalLength: 'Nominální délka',
      length: 'Délka',
      nominalVolume: 'Nominální objem',
      volume: 'Objem',
      nominalRunningMeters: 'Nominální běžné metry',
      runningMeters: 'Běžné metry',
      statusValidFrom: 'Stav platný od',
      statusValidTo: 'Stav platný do',
      comment: 'Komentář',
      timesMovedCount: 'Počet přesunů',
      distanceTravelledKm: 'Ujetá vzdálenost',
      timeCarried: 'Doba přepravy',
      weightedDistanceTravelledKm: 'Vážená ujetá vzdálenost',
      createdAt: 'Vytvořeno',
      piecesCount: 'Kusy',
      layers: 'Vrstvy',
      piecesPerLayer: 'Kusů na vrstvu',
      spacerHeight: 'Výška prokladu',
      composition: 'Složení',
    },
    compositions: {
      Empty: 'Prázdný',
      Uniform: 'Jednotný',
      Mixed: 'Smíšený',
    },
    panel: {
      pickupTitle: 'Vyzvednutí',
      placementTitle: 'Odložit',
      noNearbyPackages: 'V okolí nejsou žádné zásilky',
      noNearbyPlaces: 'V okolí nejsou žádná úložná místa',
      number: 'Číslo',
      accessibilityRating: 'Přístupnost',
      distance: 'Vzdálenost',
      accessible: 'Přístupné',
      access: 'Přístup',
      reason: 'Důvod',
      yes: 'Ano',
      no: 'Ne',
      front: 'Vpředu',
      right: 'Vpravo',
      back: 'Vzadu',
      left: 'Vlevo',
      pickup: 'Vyzvednout',
      putdown: 'Odložit',
      pickupLimitReached: 'Dosaženo limitu pro vyzvednutí',
      carriedPackages: 'Přepravené zásilky',
      nothingCarried: 'Žádné zásilky nebyly přepraveny',
      occupiesPlaces: (count: number) => `Occupies ${count} place(s)`,
      doesNotFit: 'Zásilka se nevejde do dostupných míst',
      notInAccessDirection: 'Zásilka není ve směru přístupu',
      pickupBlockedReasonPlaceNotAccessible:
        'Vybrané úložné místo neumožňuje vyzvednutí z žádné strany',
      pickupBlockedReasonWrongAccessSide: 'Přistupte k zásilce z přístupné strany',
      pickupBlockedReasonPackageOrientationMismatch:
        'Směr vyzvednutí vozidlem neodpovídá orientaci zásilky',
      approachAccessibleSide: 'Přibližte se k místu z přístupné strany',
    },
    details: {
      title: 'Detail zásilky',
      summary: 'Přehled',
      dimensions: 'Objem a rozměry',
      contents: 'Obsah',
      noContents: 'Žádný obsah',
      status: 'Stav',
      packageType: 'Typ zásilky',
      storageArea: 'Skladovací prostor',
      quality: 'Kvalita',
      age: 'Stáří',
      ageDays: (days: number) => `${days} d.`,
      product: 'Produkt',
      productCode: 'Kód produktu',
      productGroup: 'Skupina produktů',
      pieces: 'Kusy',
      thickness: 'Tloušťka',
      nominalThickness: 'Jmenovitá tloušťka',
      moisture: 'Vlhkost',
      partNumber: 'Číslo dílu',
      codes: 'Kódy',
      resize: 'Změnit velikost',
    },
    contentTable: {
      mappingTrace: 'Záznam mapování',
      hints: {
        physical: 'Skutečně naměřené hodnoty řádku obsahu. Hmotnost v kg (celý řádek), tloušťka, šířka a délka v mm. Objem (m³) a běžné metry (m) se počítají z počtu kusů a rozměrů.',
        nominal: 'Zadané cílové hodnoty řádku obsahu. Hmotnost v kg (celý řádek), tloušťka, šířka a délka v mm. Objem (m³) a běžné metry (m) se počítají z počtu kusů a rozměrů.',
        moisture: 'Vlhkost v %.',
        quality: 'Třída kvality obsahu. Ve výchozím stavu převzata z produktu.',
        partNumber: 'Identifikátor tohoto dílu v rámci balíku.',
        mappingTrace: 'Mapování produktu, ze kterého tento obsah vznikl.',
        codes: 'Identifikační kódy, např. čárové kódy, tohoto obsahu. Klikněte pro úpravu.',
      },
      inputs: {
        weight: 'Hmotnost (kg)',
        thickness: 'Tloušťka (mm)',
        width: 'Šířka (mm)',
        length: 'Délka (mm)',
        volume: 'Objem (m³), počítá se při zadání počtu kusů, tloušťky, šířky a délky',
        runningMeters: 'Běžné metry (m), počítají se při zadání počtu kusů a délky',
      },
    },
  },
  packaging: {
    actions: {
      fetchMany: {
        title: 'Načítání balení',
        error: 'Načtení balení se nezdařilo',
      },
      create: {
        title: 'Vytváření balení',
        error: 'Vytvoření balení se nezdařilo',
      },
      update: {
        title: 'Aktualizace balení',
        error: 'Aktualizace balení se nezdařila',
      },
      delete: {
        title: 'Odstranění balení',
        error: 'Odstranění balení se nezdařilo',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Delete packaging ${name}`,
      description: 'Opravdu chcete toto balení odstranit?',
      deleteButton: 'Ano, odstranit balení',
    },
    packaging: 'Balení',
    addNewPackaging: 'Přidat balení',
    editPackaging: 'Upravit balení',
  },
  packageTypes: {
    actions: {
      fetchMany: {
        title: 'Načítání typů balení',
        error: 'Načtení typů balení se nezdařilo',
      },
      create: {
        title: 'Vytváření typu balíku',
        error: 'Vytvoření typu balíku se nezdařilo',
      },
      update: {
        title: 'Aktualizace typu balíku',
        error: 'Aktualizace typu balíku se nezdařila',
      },
      delete: {
        title: 'Odstranění typu balíku',
        error: 'Odstranění typu balíku se nezdařilo',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Delete package type ${name}`,
      description: 'Opravdu chcete tento typ balíku odstranit?',
      deleteButton: 'Ano, odstranit typ balíku',
    },
    packageTypes: 'Typ balíku',
    addNewPackageType: 'Přidat typ balíku',
    editPackageType: 'Upravit typ balíku',
  },
  units: {
    millimeters: 'mm',
    centimeters: 'cm',
    meters: 'm',
    cubicMeter: 'm³',
    kilogram: 'kg',
    ton: 'tuna',
    percent: '%',
    kgPerM3: 'kg/m³',
  },
  coils: {
    actions: {
      fetchMany: {
        title: 'Načítání svitků',
        error: 'Načtení svitků se nezdařilo',
      },
      fetch: {
        title: 'Načítání svitku',
        error: 'Načtení svitku se nezdařilo',
      },
      create: {
        title: 'Vytvoření svitku',
        error: 'Vytvoření svitku se nezdařilo',
      },
      update: {
        title: 'Aktualizace svitku',
        error: 'Aktualizace svitku se nezdařila',
      },
      delete: {
        title: 'Odstranění svitku',
        error: 'Odstranění svitku se nezdařilo',
      },
      undoMove: {
        title: 'Zrušit přesun svitku',
        error: 'Zrušení přesunu svitku se nezdařilo',
      },
    },

    page: {
      title: 'Svitky',
      notFound: 'Nebyly nalezeny žádné svitky',
      loading: 'Načítání svitků',
    },

    deleteDialog: {
      header(name: string) {
        return `Delete coil ${name}`;
      },
      description: 'Opravdu chcete tento svitek odstranit? Tuto akci nelze vrátit zpět.',
      deleteButton: 'Ano, smazat svitek',
    },

    editDialog: {
      create: {
        title: 'Vytvořit nový svitek',
      },
      edit: {
        title: 'Upravit svitek',
      },
    },

    slots: 'Počet slotů',
    slotWidth: 'Šířka slotu',
    maxHeight: 'Max. výška',
    maxLevels: 'Max. počet úrovní',
    diameter: 'Průměr',
    depth: 'Hloubka',
    product: 'Produkt',
    storageArea: 'Skladovací prostor',
    position: 'Poloha',
    level: 'Úroveň',
    maxCoilDiameter: 'Max. průměr svitku',

    moveDialog: {
      title: 'Přesunout svitek',
    },

    unableToDrawCoils: (coils: string[]) => {
      if (coils.length === 1) {
        return `${coils[0]} is on a invalid position inside the storage area`;
      } else {
        return 'Mnoho svitků má v rámci skladovacího prostoru neplatné polohy';
      }
    },
  },
  logYard: {
    moveDialog: {
      title: 'Přesunout polena',
    },
    logs: 'polena',
  },
  bulk: {
    actions: {
      fetch: {
        title: 'Načítání hromadné položky',
        error: 'Načtení hromadné položky se nezdařilo',
      },
      create: {
        title: 'Vytváření hromadné položky',
        error: 'Vytvoření hromadné položky se nezdařilo',
      },
      update: {
        title: 'Aktualizace hromadné položky',
        error: 'Aktualizace hromadné položky se nezdařila',
      },
      delete: {
        title: 'Odstranění hromadného záznamu',
        error: 'Načtení hromadného záznamu se nezdařilo',
      },
      undoMove: {
        title: 'Zrušit hromadný přesun',
        error: 'Zrušení hromadného přesunu se nezdařilo',
      },
    },
    maxWeight: 'Max. hmotnost',
    maxVolume: 'Max. objem',

    bulkUnit(value?: Unit | null): string {
      switch (value) {
        case Unit.Kilogram:
          return 'kilogram';
        case Unit.CubicMeter:
          return 'kubické metry';
        case Unit.LongTon:
          return 'dlouhé tuny';
        case Unit.MetricTon:
          return 'metrické tuny';
        case Unit.Pound:
          return 'libry';
        case Unit.ShortTon:
          return 'krátké tuny';
        default:
          return '';
      }
    },
    bulkUnitShort(value?: Unit | null): string {
      switch (value) {
        case Unit.MetricTon:
          return 't';
        case Unit.Kilogram:
          return 'kg';
        case Unit.CubicMeter:
          return 'm³';
        case Unit.LongTon:
          return 'l.tn';
        case Unit.Pound:
          return 'lbs';
        case Unit.ShortTon:
          return 'tn';
        default:
          return '';
      }
    },
    moveDialog: {
      title: 'Přesunout hromadný záznam',
    },
  },
  dashboard: {
    actions: {
      fetchMany: {
        title: 'Načítání dashboardů',
        error: 'Načtení dashboardů se nezdařilo',
      },
      fetch: {
        title: 'Načítání dashboardu',
        error: 'Načtení dashboardu se nezdařilo',
      },
      create: {
        title: 'Vytváření dashboardu',
        error: 'Vytvoření dashboardu se nezdařilo',
      },
      update: {
        title: 'Aktualizace dashboardu',
        error: 'Aktualizace dashboardu se nezdařila',
      },
      delete: {
        title: 'Odstranění dashboardu',
        error: 'Odstranění dashboardu se nezdařilo',
        statusCodes: {
          409: 'Odstranění dashboardu se nezdařilo',
        },
      },
    },
    listPage: {
      title: 'Dashboardy',
      noFound: 'Nebyly nalezeny žádné dashboardy',
      loading: 'Načítání dashboardů...',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete dashboard ${name}`;
      },
      description: 'Opravdu chcete tento dashboard odstranit? Tuto akci nelze vrátit zpět.',
      deleteButton: 'Ano, odstranit dashboard',
    },
    editPage: {
      editTitle: 'Upravit dashboard',
      createTitle: 'Vytvořit dashboard',
    },
    editComponentDialog: {
      editTitle: 'Upravit komponentu',
      addTitle: 'Přidat komponentu',
    },
    productGroupTable: {
      title: 'Skladové zásoby podle skupiny produktů',
    },
    productTable: {
      title: 'Skladové zásoby podle produktu',
    },
    productGroupDiagram: {
      title: 'Skladové zásoby podle skupiny produktů',
    },
    noDashboards: 'Žádné dashboardy nejsou k dispozici',
  },
  overview: {
    title: 'Přehled',
  },
  releaseNotes: {
    actions: {
      fetchLatest: {
        title: 'Načítání nejnovějších poznámek k verzi',
        error: 'Načtení nejnovějších poznámek k verzi se nezdařilo',
      },
      fetchMany: {
        title: 'Načítání poznámek k verzi',
        error: 'Načtení poznámek k verzi se nezdařilo',
      },
    },
  },
  keybinds: {
    action: 'Akce',
    keybind: 'Klávesová zkratka',
  },
  reportPage: {
    selectReport: 'Vybrat sestavu',
    showReport: 'Zobrazit sestavu',
    selectExportFormat: 'Vybrat formát exportu',
    exportReport: 'Exportovat sestavu',
    exportError: 'Export sestavy se nezdařil',
    showError: 'Zobrazení sestavy se nezdařilo',
  },
  mapTable: {
    col: {
      name: 'Název',
      type: 'Typ',
    },
  },
  dataTable: {
    filterBy: 'Filtrovat podle',
    clearFilters: 'Vymazat filtry',
    showing: 'zobrazuje',
    of: 'z',
    filtered: 'filtrovaných',
  },
  primeVueLocale: {
    startsWith: 'Začíná na',
    contains: 'Obsahuje',
    notContains: 'Neobsahuje',
    endsWith: 'Končí na',
    equals: 'Rovná se',
    notEquals: 'Nerovná se',
    noFilter: 'Žádný filtr',

    lt: 'Menší než',
    lte: 'Menší nebo rovno',
    gt: 'Větší než',
    gte: 'Větší nebo rovno',

    dateIs: 'Datum je',
    dateIsNot: 'Datum není',
    dateBefore: 'Datum je před',
    dateAfter: 'Datum je po',

    clear: 'Vymazat',
    apply: 'Použít',

    matchAll: 'Splnit všechny',
    matchAny: 'Splnit libovolné',
    addRule: 'Přidat pravidlo',
    removeRule: 'Odstranit pravidlo',

    accept: 'Přijmout',
    reject: 'Odmítnout',

    choose: 'Vybrat',
    upload: 'Nahrát',
    cancel: 'Zrušit',
    completed: 'Dokončeno',
    pending: 'Čeká na zpracování',

    fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB'],

    dayNames: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
    dayNamesShort: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
    dayNamesMin: ['Neděle', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],

    monthNames: [
      'Leden',
      'Únor',
      'Březen',
      'Duben',
      'Květen',
      'Červen',
      'Červenec',
      'Srpen',
      'Září',
      'Říjen',
      'Listopad',
      'Prosinec',
    ],
    monthNamesShort: [
      'Leden',
      'Únor',
      'Březen',
      'Duben',
      'Květen',
      'Červen',
      'Červenec',
      'Srpen',
      'Září',
      'Říjen',
      'Listopad',
      'Prosinec',
    ],

    chooseYear: 'Vyberte rok',
    chooseMonth: 'Vyberte měsíc',
    chooseDate: 'Vyberte datum',

    prevDecade: 'Předchozí desetiletí',
    nextDecade: 'Následující desetiletí',
    prevYear: 'Předchozí rok',
    nextYear: 'Následující rok',
    prevMonth: 'Předchozí měsíc',
    nextMonth: 'Následující měsíc',
    prevHour: 'Předchozí hodina',
    nextHour: 'Následující hodina',
    prevMinute: 'Předchozí minuta',
    nextMinute: 'Následující minuta',
    prevSecond: 'Předchozí vteřina',
    nextSecond: 'Následující vteřina',

    am: 'Dopoledne',
    pm: 'Odpoledne',
    today: 'Dnes',
    weekHeader: 'Týden',

    firstDayOfWeek: 0,
    showMonthAfterYear: false,
    dateFormat: 'mm/dd/rr',

    weak: 'Slabé',
    medium: 'Střední',
    strong: 'Silné',
    passwordPrompt: 'Zadejte heslo',

    emptyFilterMessage: 'Nebyly nalezeny žádné výsledky',
    searchMessage: 'K dispozici {0} výsledků',
    selectionMessage: 'Vybráno {0} položek',
    emptySelectionMessage: 'Nebyly vybrány žádné položky',
    emptySearchMessage: 'Nebyly nalezeny žádné výsledky',
    emptyMessage: 'Nejsou k dispozici žádná data',

    fileChosenMessage: '{0} souborů',
    noFileChosenMessage: 'Nebyl vybrán žádný soubor',
  },
  delivery: {
    plannedStart: 'Plánovaný začátek',
    plannedFinish: 'Plánovaný konec',
    actualStart: 'Skutečný začátek',
    actualFinish: 'Skutečný konec',
  },
  inboundDelivery: {
    actions: {
      fetchMany: {
        title: 'Načítání příchozích dodávek',
        error: 'Načtení příchozích dodávek se nezdařilo',
      },
      fetch: {
        title: 'Načítání příchozích dodávek',
        error: 'Načtení příchozích dodávek se nezdařilo',
      },
      create: {
        title: 'Vytváření příchozích dodávek',
        error: 'Vytvoření příchozích dodávek se nezdařilo',
      },
      update: {
        title: 'Aktualizace příchozích dodávek',
        error: 'Aktualizace příchozích dodávek se nezdařila',
      },
      delete: {
        title: 'Mazání příchozích dodávek',
        error: 'Mazání příchozích dodávek se nezdařilo',
        statusCodes: {
          409: 'Mazání příchozích dodávek se nezdařilo',
        },
      },
      transaction: {
        title: 'Vytváření transakce příchozí dodávky',
        error: 'Vytvoření transakce příchozí dodávky se nezdařilo',
      },
      fetchTransactions: {
        title: 'Načítání transakcí pro dodávku',
        error: 'Načtení transakcí pro dodávku se nezdařilo',
      },
    },
    listPage: {
      title: 'Příchozí dodávky',
      noFound: 'Žádná dodávka nalezena',
      loading: 'Načítání dodávek...',
      transactions: 'Zobrazit transakce',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete delivery ${name}`;
      },
      description: 'Opravdu chcete tuto dodávku smazat?',
      deleteButton: 'Ano, smazat dodávku',
    },
    editPage: {
      editTitle: 'Upravit příchozí dodávku',
      createTitle: 'Vytvořit příchozí dodávku',
      rowsTitle: 'Řádky',
      addRow: 'Přidat řádek',
      noRows: 'Žádné řádky přidány',
      productRequired: 'U všech řádků příchozích dodávek je vyžadován produkt.',
      rowWeight: 'Hmotnost',
      rowVolume: 'Objem',
      rowDeliveredWeight: 'Dodaná hmotnost',
      rowDeliveredVolume: 'Dodaný objem',
    },
    transactionDialog: {
      openButton: 'Přidat příchozí transakci',
      title: 'Přidat příchozí transakci',
      filterMode: 'Filtr dodávek',
      filterMatchingStorageArea: 'Odpovídající skladové místo',
      filterAllDeliveries: 'Všechny dodávky',
      delivery: 'Dodávka',
      deliveryRow: 'Řádek dodávky',
      selectDelivery: 'Vybrat dodávku',
      selectDeliveryRow: 'Vybrat řádek dodávky',
      selectStorageArea: 'Vybrat skladové místo',
      deliveryRequired: 'Dodávka je povinná.',
      deliveryRowRequired: 'Řádek dodávky je povinný.',
      storageAreaRequired: 'Skladové místo je povinné.',
      weightRequired: 'Hmotnost musí být větší než 0.',
    },
    transactionListDialog: {
      title: 'Transakce pro dodávku',
      noFound: 'Žádné transakce nenalezeny',
      loading: 'Načítání transakcí',
      transactionTime: 'Časová značka',
    },
  },
  outboundDelivery: {
    actions: {
      fetchMany: {
        title: 'Načítání odchozích dodávek',
        error: 'Nepodařilo se načíst odchozí dodávky',
      },
      fetch: {
        title: 'Načítání odchozích dodávek',
        error: 'Nepodařilo se načíst odchozí dodávky',
      },
      create: {
        title: 'Vytváření odchozích dodávek',
        error: 'Nepodařilo se vytvořit odchozí dodávky',
      },
      update: {
        title: 'Aktualizace odchozích dodávek',
        error: 'Nepodařilo se aktualizovat odchozí dodávky',
      },
      delete: {
        title: 'Mazání odchozích dodávek',
        error: 'Nepodařilo se smazat odchozí dodávky',
        statusCodes: {
          409: 'Nepodařilo se smazat odchozí dodávky',
        },
      },
      transaction: {
        title: 'Vytváření transakce odchozí dodávky',
        error: 'Nepodařilo se vytvořit transakci odchozí dodávky',
      },
      fetchTransactions: {
        title: 'Načítání transakcí pro dodávku',
        error: 'Nepodařilo se načíst transakce pro dodávku',
      },
    },
    listPage: {
      title: 'Odchozí dodávky',
      noFound: 'Žádná dodávka nenalezena',
      loading: 'Načítání dodávek...',
      transactions: 'Zobrazit transakce',
    },
    deleteDialog: {
      header(name: string) {
        return `Smazat dodávku ${name}`;
      },
      description: 'Opravdu chcete smazat tuto dodávku?',
      deleteButton: 'Ano, smazat dodávku',
    },
    editPage: {
      editTitle: 'Upravit odchozí dodávku',
      createTitle: 'Vytvořit odchozí dodávku',
      rowsTitle: 'Řádky',
      addRow: 'Přidat řádek',
      noRows: 'Žádné řádky přidány',
      productRequired: 'U všech řádků odchozích dodávek je vyžadován produkt.',
      rowWeight: 'Hmotnost',
      rowVolume: 'Objem',
      rowDeliveredWeight: 'Dodaná hmotnost',
      rowDeliveredVolume: 'Dodaný objem',
    },
    transactionDialog: {
      openButton: 'Přidat transakci odchozí dodávky',
      title: 'Přidat transakci odchozí dodávky',
      filterMode: 'Filtr dodávek',
      filterMatchingStorageArea: 'Shodná skladovací oblast',
      filterAllDeliveries: 'Všechny dodávky',
      delivery: 'Dodávka',
      deliveryRow: 'Řádek dodávky',
      selectDelivery: 'Vybrat dodávku',
      selectDeliveryRow: 'Vybrat řádek dodávky',
      selectStorageArea: 'Vybrat skladovací oblast',
      deliveryRequired: 'Dodávka je povinná.',
      deliveryRowRequired: 'Řádek dodávky je povinný.',
      storageAreaRequired: 'Skladovací oblast je povinná.',
      weightRequired: 'Hmotnost je povinná.',
    },
    transactionListDialog: {
      title: 'Transakce pro dodávku',
      noFound: 'Žádné transakce nenalezeny',
      loading: 'Načítání transakcí',
      transactionTime: 'Časová značka',
    },
  },
};
